import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BASE_SERVER_URL, IS_CAPTCHA_ENABLED } from 'src/config';
import { languages } from 'src/content/Settings/Profile/languages';
import useAnalytics from 'src/hooks/useAnalytics';
import { setAlert } from 'src/redux/slices/snackbar';
import { useDispatch } from 'src/redux/store';
import { ALERT } from 'src/types/enum';
import {
  SERVER_NOT_REACHABLE,
  SIGNUP_CONTINUE_CLICKED,
  SIGNUP_RESEND_EMAIL_FAILED,
  SIGNUP_RESEND_EMAIL_SENT_SUCCESSFULL,
  SIGNUP_RESEND_VERIFICATION_EMAIL_SENT,
  SIGNUP_VERIFICATION_EMAIL_SENT
} from 'src/utils/analyticsUtils';
import axios from 'src/utils/axios';
import { EMAIL_REG_EXP, SERVER_DOWN_CODES } from 'src/utils/constants';
import { ErrorContext, ErrorMessages } from 'src/utils/errorMappings';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { validateEmailField, sanitizeEmailInput } from 'src/utils/utils';

type VerifyEmailErrorResponse = {
  response?: {
    status?: number;
    data?: { extended?: { error_name?: string } };
  };
};

const handleVerifyEmailError = (
  error: unknown,
  options: {
    dispatch: ReturnType<typeof useDispatch>;
    t: (key: string) => string;
    collectAnalytics: (event: string) => void;
    trackServerUnavailable?: boolean;
    fallbackMessageKey: string;
  }
) => {
  const {
    dispatch,
    t,
    collectAnalytics,
    trackServerUnavailable = false,
    fallbackMessageKey
  } = options;

  const response = (error as VerifyEmailErrorResponse)?.response;
  const status = response?.status;
  const errorCode = response?.data?.extended?.error_name;
  const isServerUnavailable =
    !response ||
    (status !== undefined &&
      status !== null &&
      SERVER_DOWN_CODES.includes(status));

  if (isServerUnavailable && trackServerUnavailable) {
    collectAnalytics(SERVER_NOT_REACHABLE);
  }

  const selfSignUpMessages = ErrorMessages[ErrorContext.SELF_SIGN_UP];
  const message = isServerUnavailable
    ? t(T.serverNotReachable)
    : t(selfSignUpMessages?.[errorCode] ?? fallbackMessageKey);

  dispatch(
    setAlert({
      msg: message,
      type: ALERT.ERROR
    })
  );
};

type UseSignUpSectionOptions = {
  onSendVerifyError?: () => void;
};

export const useSignUpSection = (options: UseSignUpSectionOptions = {}) => {
  const { onSendVerifyError } = options;
  const collectAnalytics = useAnalytics();
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isMarketingConsent, setIsMarketingConsent] = useState<boolean>(true);
  const {
    t,
    i18n: { language }
  } = useTranslation();

  const dispatch = useDispatch();

  const currentLang = languages.find((lang) => lang.code === language);

  const handleVerifyEmailCatch = (error: unknown) => {
    handleVerifyEmailError(error, {
      dispatch,
      t,
      collectAnalytics,
      trackServerUnavailable: true,
      fallbackMessageKey: T.failedToSendVerificationEmail
    });
  };

  const resendVerificationEmail = (marketingConsent: boolean) => {
    if (!EMAIL_REG_EXP.test(email)) {
      setEmailError(T.invalidEmail);
      return;
    }

    const url = `${BASE_SERVER_URL}/api/v1/tenant/verify-email`;
    collectAnalytics(SIGNUP_RESEND_VERIFICATION_EMAIL_SENT);
    const formData = new FormData();
    formData.append('email', email);
    formData.append('language', currentLang?.code || 'en');
    formData.append('marketing_consent', `${marketingConsent}`);
    setIsLoading(true);
    axios
      .patch(url, formData)
      .then((response) => {
        if (response.status === 200) {
          collectAnalytics(SIGNUP_RESEND_EMAIL_SENT_SUCCESSFULL);
          dispatch(
            setAlert({
              msg: t(T.emailVerificationSuccessfullySend),
              type: ALERT.SUCCESS
            })
          );
        } else {
          collectAnalytics(SIGNUP_RESEND_EMAIL_FAILED);
          dispatch(
            setAlert({
              msg: t(T.email),
              type: ALERT.ERROR
            })
          );
        }
      })
      .catch(handleVerifyEmailCatch)
      .finally(() => setIsLoading(false));
  };

  const sendVerifyEmail = (marketingConsent: boolean) => {
    const validationError = validateEmailField(email?.trim());
    if (validationError) {
      setEmailError(validationError);
      setRecaptchaToken(null);
      return;
    }
    if (!recaptchaToken && IS_CAPTCHA_ENABLED) {
      dispatch(
        setAlert({
          msg: t(T.recaptchaRequired),
          type: ALERT.ERROR
        })
      );
      setRecaptchaToken(null);
      return;
    }
    collectAnalytics(SIGNUP_CONTINUE_CLICKED);

    const url = `${BASE_SERVER_URL}/api/v1/tenant/verify-email`;

    const formData = new FormData();
    formData.append('email', email);
    formData.append('language', currentLang?.code || 'en');
    formData.append('marketing_consent', `${marketingConsent}`);
    if (IS_CAPTCHA_ENABLED) {
      formData.append('recaptcha_response', recaptchaToken);
    }
    setIsLoading(true);
    axios
      .post(url, formData)
      .then((response) => {
        if (response.status === 200) {
          if (response?.data?.ret_val?.data?.self_signup_required) {
            collectAnalytics(SIGNUP_VERIFICATION_EMAIL_SENT);
            dispatch(
              setAlert({
                msg: t(T.emailVerificationSuccessfullySend),
                type: ALERT.SUCCESS
              })
            );
            setIsVerifying(true);
            return;
          } else {
            dispatch(
              setAlert({
                msg: t(T.userAlreadyExists),
                type: ALERT.ERROR
              })
            );
          }
        }
      })
      .catch((error: unknown) => {
        handleVerifyEmailCatch(error);
        onSendVerifyError?.();
      })
      .finally(() => {
        setIsLoading(false);
        setRecaptchaToken(null);
      });
  };

  const handleEmail = ({ target: { value } }) => {
    setEmailError('');
    setEmail(sanitizeEmailInput(value));
  };

  return {
    email,
    emailError: emailError ? t(emailError as any) : '',
    isMarketingConsent,
    setEmail,
    handleEmail,
    resendVerificationEmail,
    sendVerifyEmail,
    setIsVerifying,
    isVerifying,
    isLoading,
    setIsMarketingConsent,
    setRecaptchaToken,
    recaptchaToken
  };
};
